/**
 * Ayurveda dosha-aligned sleep window. 5-question intake, then renders
 * the recommended bedtime/wake clock vs the user's observed window.
 */
import { classifyDosha, profileForDosha, solarFor, alignedSleepWindow, formatClock, type DoshaQuestionnaire, type Dosha } from '../chassis/ayurveda.ts';
import { store } from '../store.ts';

const PROFILE_KEY = 'aramgah:dosha-v1';

interface SavedProfile {
  dosha: Dosha;
  geo: { lat: number; lon: number };
  ts: string;
}

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
    container-type: inline-size;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background: linear-gradient(180deg, oklch(11% 0.04 270 / 0.55), oklch(8% 0.03 268 / 0.65));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(22px, 5vw, 30px);
    color: oklch(94% 0.04 88);
    letter-spacing: -0.01em;
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 14px;
  }
  fieldset {
    border: none;
    padding: 0;
    margin: 12px 0;
  }
  legend {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 8px;
  }
  .choices {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .choice {
    border: 1px solid oklch(80% 0.04 80 / 0.18);
    background: transparent;
    color: oklch(86% 0.04 80);
    padding: 9px 14px;
    border-radius: 999px;
    font-family: var(--display);
    font-style: italic;
    font-size: 14px;
    cursor: pointer;
  }
  .choice.active {
    background: oklch(86% 0.14 82);
    color: oklch(12% 0.04 270);
    border-color: oklch(86% 0.14 82);
  }
  .clock-card {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    margin-top: 18px;
    padding: 16px 18px;
    border-radius: 12px;
    border: 1px solid oklch(80% 0.04 80 / 0.16);
    background: oklch(7% 0.02 270 / 0.5);
  }
  .clock-num {
    font-family: var(--display);
    font-weight: 500;
    font-size: 38px;
    color: oklch(94% 0.04 88);
    letter-spacing: -0.02em;
  }
  .clock-lbl {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(64% 0.04 60);
  }
  .rationale {
    margin-top: 14px;
    font-family: var(--display);
    font-style: italic;
    color: oklch(82% 0.05 60);
    font-size: 14px;
  }
  button.compute {
    margin-top: 14px;
    background: oklch(86% 0.14 82);
    color: oklch(12% 0.04 270);
    border: none;
    padding: 12px 22px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    cursor: pointer;
  }
  .saved-tag {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: oklch(64% 0.06 90);
    text-align: right;
    margin-top: 10px;
  }
  @container (min-width: 520px) {
    .clock-card { grid-template-columns: repeat(4, 1fr); }
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · Dosha-aligned window</div>
  <h3>Aapka prakriti, aapka sone ka samay</h3>
  <div class="sub">Paanch sawaal · Ashtanga Hridayam ke aadhaar par</div>

  <div id="q-stage">
    <fieldset data-q="body_frame">
      <legend>1 · Sharir ka dhancha</legend>
      <div class="choices">
        <button class="choice" data-v="thin">patla, halka</button>
        <button class="choice" data-v="medium">madhyam, gathila</button>
        <button class="choice" data-v="sturdy">bhaari, mota</button>
      </div>
    </fieldset>
    <fieldset data-q="hunger">
      <legend>2 · Bhookh ka rang</legend>
      <div class="choices">
        <button class="choice" data-v="irregular">kabhi yahaan, kabhi wahaan</button>
        <button class="choice" data-v="sharp">tez, samay par</button>
        <button class="choice" data-v="slow">der se aati hai</button>
      </div>
    </fieldset>
    <fieldset data-q="sleep_natural">
      <legend>3 · Saadharantah neend kaisi</legend>
      <div class="choices">
        <button class="choice" data-v="light">halki, tooti</button>
        <button class="choice" data-v="sound">gehri</button>
        <button class="choice" data-v="heavy">bahut zyada</button>
      </div>
    </fieldset>
    <fieldset data-q="energy">
      <legend>4 · Urja ka tareeqa</legend>
      <div class="choices">
        <button class="choice" data-v="bursts">bursts mein</button>
        <button class="choice" data-v="steady">samaan</button>
        <button class="choice" data-v="consistent_slow">lagatar par dheemi</button>
      </div>
    </fieldset>
    <fieldset data-q="temperament">
      <legend>5 · Mizaaj</legend>
      <div class="choices">
        <button class="choice" data-v="creative_anxious">creative · halka chinta</button>
        <button class="choice" data-v="driven_irritable">tez · zara zid</button>
        <button class="choice" data-v="calm_attached">shaant · jod-todna pasand nahi</button>
      </div>
    </fieldset>
    <button class="compute" id="compute" disabled>Hisaab lagao</button>
  </div>

  <div id="result-stage" hidden>
    <div class="clock-card">
      <div>
        <div class="clock-lbl">Dosha</div>
        <div class="clock-num" id="dosha-name">—</div>
      </div>
      <div>
        <div class="clock-lbl">Bed</div>
        <div class="clock-num" id="bed">—</div>
      </div>
      <div>
        <div class="clock-lbl">Wake</div>
        <div class="clock-num" id="wake">—</div>
      </div>
      <div>
        <div class="clock-lbl">Brahma Muhurta</div>
        <div class="clock-num" id="brahma">—</div>
      </div>
    </div>
    <div class="rationale" id="rationale">—</div>
    <div class="saved-tag" id="saved-tag" hidden></div>
  </div>
</div>
`;

export class AramgahDoshaWindow extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #answers: Partial<DoshaQuestionnaire> = {};

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#shadow.querySelectorAll<HTMLButtonElement>('.choice').forEach((btn) => {
      btn.addEventListener('click', () => this.#pick(btn));
    });
    const compute = this.#shadow.getElementById('compute');
    compute?.addEventListener('click', () => this.#compute());
    const saved = loadSavedProfile();
    if (saved) this.#renderResult(saved.dosha, saved.geo, true);
  }

  #pick(btn: HTMLButtonElement): void {
    const set = btn.closest('fieldset') as HTMLFieldSetElement | null;
    if (!set) return;
    const q = set.dataset['q'] as keyof DoshaQuestionnaire | undefined;
    const v = btn.dataset['v'] as DoshaQuestionnaire[keyof DoshaQuestionnaire] | undefined;
    if (!q || !v) return;
    set.querySelectorAll<HTMLButtonElement>('.choice').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    (this.#answers as Record<string, string>)[q] = v;
    const compute = this.#shadow.getElementById('compute') as HTMLButtonElement | null;
    if (compute) compute.disabled = Object.keys(this.#answers).length < 5;
  }

  #compute(): void {
    if (Object.keys(this.#answers).length < 5) return;
    const dosha = classifyDosha(this.#answers as DoshaQuestionnaire);
    const geo = { lat: 28.6139, lon: 77.2090 };
    saveProfile({ dosha, geo, ts: new Date().toISOString() });
    this.#renderResult(dosha, geo, false);
  }

  #renderResult(dosha: Dosha, geo: { lat: number; lon: number }, fromSaved: boolean): void {
    const stage = this.#shadow.getElementById('q-stage');
    const result = this.#shadow.getElementById('result-stage');
    if (stage) stage.hidden = true;
    if (result) result.hidden = false;
    const profile = profileForDosha(dosha);
    const solar = solarFor(new Date(), geo.lat, geo.lon);
    const win = alignedSleepWindow(profile, solar);
    const setText = (id: string, v: string) => {
      const el = this.#shadow.getElementById(id);
      if (el) el.textContent = v;
    };
    setText('dosha-name', prettifyDosha(dosha));
    setText('bed', formatClock(win.bed_h));
    setText('wake', formatClock(win.wake_h));
    setText('brahma', formatClock(win.brahma_muhurta_h));
    setText('rationale', `${profile.rationale_hi} · ${profile.rationale_en}`);
    const tag = this.#shadow.getElementById('saved-tag');
    if (tag) {
      tag.hidden = false;
      tag.textContent = fromSaved ? 'saved from earlier' : 'saved on device';
    }
    store.patch({});
  }
}

function prettifyDosha(d: Dosha): string {
  return d.split('-').map((p) => p[0]?.toUpperCase() + p.slice(1)).join('-');
}

function saveProfile(p: SavedProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
}

function loadSavedProfile(): SavedProfile | null {
  const raw = localStorage.getItem(PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SavedProfile;
  } catch {
    return null;
  }
}

customElements.define('aramgah-dosha-window', AramgahDoshaWindow);
